import { useTranslation } from "react-i18next";
import type { NavSection } from "@/shared/types";
import { cn } from "@/shared/lib/utils";
import { isTransfersNavPath } from "../../config/navigation.config";
import { SidebarItem } from "./SidebarItem";

interface SidebarSectionProps {
  section: NavSection;
  isOpen: boolean;
  currentPath: string;
}

export function SidebarSection({
  section,
  isOpen,
  currentPath,
}: SidebarSectionProps) {
  const { t } = useTranslation();

  // Check if any item in this section is active
  const isItemActive = (
    itemId: string,
    href: string | undefined,
    children?: typeof section.items
  ) => {
    if (itemId === "transfers" && isTransfersNavPath(currentPath)) return true;
    if (href && currentPath.startsWith(href)) return true;
    if (children) {
      return children.some(
        (child) => child.href && currentPath.startsWith(child.href)
      );
    }
    return false;
  };

  return (
    <div className="mb-2">
      {/* Section Title */}
      {section.title && isOpen && (
        <div className="px-4 py-2 border-t border-grey-100">
          <span className="text-xs  font-medium text-grey-200 capitalize tracking-wider">
            {section.titleKey ? t(section.titleKey) : section.title}
          </span>
        </div>
      )}

      {/* Separator line when collapsed */}
      {section.title && !isOpen && (
        <div className="mx-3 my-2 border-t border-grey-100" />
      )}

      {/* Section Items */}
      <div className={cn("space-y-1", isOpen ? "px-4" : "px-2")}>
        {section.items.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            isOpen={isOpen}
            isActive={isItemActive(item.id, item.href, item.children)}
            currentPath={currentPath}
          />
        ))}
      </div>
    </div>
  );
}
